import { useId, useMemo, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import Modal from "../../../components/Modal";
import { TextInput } from "../../../components/TextInput";
import { Button } from "../../../components/Button";
import { SelectInput } from "../../../components/SelectInput/SelectInput";
import { api } from "../../../services/api";
import { ShowGenericErrorToast, ShowToast } from "../../../services/toast";
import { useApp } from "../../../services/app-context";
import { GroupTag } from "../sales-aid-modal/components/GroupTag";
import {
  buildFileUpdate,
  EditDocumentForm,
  SalesAidFileUpdate,
  toEditDocumentForm,
} from "./editDocument";
import { SalesAidFile } from "./types";

const labelClassName = "uppercase text-xs font-semibold text-gray-701";
const inputClassName = "!bg-gray-100 !border-0 !rounded-md !h-9 !p-2.5 !text-sm";

export default function EditDocumentModal({
  file,
  onClose,
  onSuccess,
}: {
  file: SalesAidFile;
  onClose: () => void;
  onSuccess: () => void;
}) {
  const { tenantProfile } = useApp();
  const cohorts = useMemo(() => tenantProfile?.groups ?? [], [tenantProfile?.groups]);
  const [form, setForm] = useState<EditDocumentForm>(() => toEditDocumentForm(file));
  const nameInputId = useId();
  const codeInputId = useId();
  const cohortInputId = useId();

  const update = useMemo(() => buildFileUpdate(file, form), [file, form]);
  const hasChanges = Object.keys(update).length > 0;
  const isNameMissing = !form.name.trim();
  const canSave = hasChanges && !isNameMissing;

  const cohortOptions = useMemo(
    () =>
      cohorts
        .filter((cohort) => !form.groups.includes(cohort.key))
        .map((cohort) => ({ label: cohort.name, value: cohort.key })),
    [cohorts, form.groups]
  );

  const { mutate, isLoading } = useMutation({
    mutationFn: (data: SalesAidFileUpdate) => api.updateSalesAidFile(file.id, data),
    onSuccess: () => {
      ShowToast({ message: "Document updated", type: "success" });
      onSuccess();
    },
    onError: () => ShowGenericErrorToast(),
  });

  return (
    <Modal
      contentClassName="!px-0 [&_.title]:!text-[#444]"
      title="Edit Document"
      isOpen
      handleModal={onClose}
    >
      <form
        className="flex flex-col gap-5 px-5 mt-4"
        data-testid="edit-document-form"
        onSubmit={(e) => {
          e.preventDefault();
          if (canSave) mutate(update);
        }}
      >
        <div className="flex flex-col gap-2">
          <label htmlFor={nameInputId} className={labelClassName}>
            File Name
          </label>
          <TextInput
            id={nameInputId}
            data-testid="edit-document-name"
            className={inputClassName}
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
          />
          {isNameMissing && (
            <span className="text-xs text-[#DC2626]">File name is required</span>
          )}
        </div>
        <div className="flex flex-col gap-2">
          <label htmlFor={codeInputId} className={labelClassName}>
            Doc Code
          </label>
          <TextInput
            id={codeInputId}
            data-testid="edit-document-code"
            className={inputClassName}
            placeholder="Code"
            value={form.code}
            onChange={(e) => setForm({ ...form, code: e.target.value })}
          />
        </div>
        <div className="flex flex-col gap-2">
          <label htmlFor={cohortInputId} className={labelClassName}>
            Cohorts
          </label>
          <SelectInput
            id={cohortInputId}
            testId="edit-document-cohort-select"
            options={cohortOptions}
            value=""
            onChange={(e) => setForm({ ...form, groups: [...form.groups, e.target.value] })}
            placeholder="Add Cohort"
            className="!h-9 !border-0 !bg-gray-100 !text-sm !rounded-md"
            containerClassName="w-full"
            disabled={cohortOptions.length === 0}
          />
          {form.groups.length > 0 ? (
            <div className="flex items-center gap-2 flex-wrap" data-testid="edit-document-cohorts">
              {form.groups.map((key) => (
                <GroupTag
                  key={key}
                  selectedGroup={cohorts.find((cohort) => cohort.key === key) ?? { name: key }}
                  onRemove={() =>
                    setForm({ ...form, groups: form.groups.filter((group) => group !== key) })
                  }
                />
              ))}
            </div>
          ) : (
            <span className="text-xs text-gray-701">
              No cohorts selected. This document will be available to all cohorts.
            </span>
          )}
        </div>
        <div className="flex justify-end mt-6 gap-2.5">
          <Button
            type="button"
            variant="primary"
            onClick={onClose}
            disabled={isLoading}
            className="!rounded-full !text-black !w-24 hover:!opacity-80 !py-2.5 !bg-gray-201"
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="primary"
            data-testid="edit-document-save"
            loading={isLoading}
            disabled={!canSave}
            className="!rounded-full !min-w-24 !py-2.5"
          >
            Save
          </Button>
        </div>
      </form>
    </Modal>
  );
}
